<script setup lang="ts">
import type { CmsElementProductListing } from "@shopware-pwa/composables-next";
import { useCmsTranslations } from "@shopware-pwa/composables-next";
import ProductCardSkeleton from "../skeleton/ProductCardSkeleton.vue";
import { useCategoryListing } from "#imports";
import { computed, ref, watch } from "vue";
import { defu } from "defu";
import { useRoute, useRouter } from "vue-router";
import type { Schemas, operations } from "#shopware";
import { getProductList } from "~/server/apiBuilder";
import { productListTracking } from "~/components/utilities/GtmTrackings";
import { useProductListingFilter } from "@/composables/useProductListingFilter";
import { useHistoryNavigation } from '~/composables/useHistoryNavigation';

const props = defineProps<{
  content: CmsElementProductListing;
}>();

const { content } = props;
const { data } = content;

const defaultLimit = 24;
const defaultPage = 1;
const defaultOrder = "name-asc";
const isLoading = ref(false);
const categoryId = props.content.data.listing.currentFilters.navigationId;

const { show, hide,isActive } = useProductListingFilter();

type Translations = {
  listing: {
    noProducts: string;
    perPage: string;
    product: string;
    products: string;
  };
};
let translations: Translations = {
  listing: {
    noProducts: "No products found 😔",
    perPage: "Per Page:",
    product: "Product",
    products: "Products",
  },
};
translations = defu(useCmsTranslations(), translations) as Translations;

const {
  changeCurrentPage,
  getCurrentPage,
  getElements,
  getTotalPagesCount,
  loading,
  setInitialListing,
  getCurrentSortingOrder,
  changeCurrentSortingOrder,
  getTotal,
  getSortingOrders,
  getCurrentFilters,
} = useCategoryListing();
const route = useRoute();
const router = useRouter();
const limit = ref(
  route.query.limit
    ? Number(route.query.limit)
    : props.content?.data?.listing?.limit
      ? Number(props.content?.data?.listing?.limit)
      : defaultLimit
);


const changePage = async (page: number) => {
  await router.push({
    query: {
      ...route.query,
      p: page,
      limit: limit.value,
    },
  });
  await changeCurrentPage(
    page,
    route.query as unknown as operations["searchPage post /search"]["body"]
  );
};

const toggleFilter = () => {
  if (isActive.value) {
    hide();
  } else {
    show();
  }
};

const currentSortingOrder = computed({
  get: (): string => getCurrentSortingOrder.value || "",
  set: async (order: string): Promise<void> => {
    await router.push({
      query: {
        ...route.query,
        order,
      },
    });

    await changeCurrentSortingOrder(order, {
      ...(route.query as unknown as operations["searchPage post /search"]["body"]),
      limit: route.query.limit ? Number(route.query.limit) : 15,
    });
  },
});

const changeLimit = async (limit: Event) => {
  const select = limit.target as HTMLSelectElement;

  await router.push({
    query: {
      ...route.query,
      limit: select.value,
      p: defaultPage,
    },
  });
  await changeCurrentPage(
    defaultPage,
    route.query as unknown as operations["searchPage post /search"]["body"]
  );
};

const isProductListing = computed(
  () => props.content?.type === "product-listing"
);
// This is a workaround because vercel caching with the nuxt preset does not support query params at the moment
// @see https://github.com/shopware/frontends/issues/687#issuecomment-1988392091
const compareRouteQueryWithInitialListing = async () => {
  const {
    limit: limitListing = defaultLimit,
    page: pageListing = defaultPage,
    sorting: orderListing = defaultOrder,
  } = data?.listing ?? {};

  const { limit: queryLimit, page: queryPage, order: queryOrder } = route.query;

  const isChangePageNeeded =
    (queryLimit && limit.value !== limitListing) ||
    (queryPage && Number(queryPage) !== pageListing) ||
    (queryOrder && queryOrder !== orderListing)
      ? true
      : false;

  if (isChangePageNeeded) {
    const limitQuery = queryLimit ? Number(queryLimit) : defaultLimit;
    const pageQuery = queryPage ? Number(queryPage) : defaultPage;
    const orderQuery = queryOrder ? (queryOrder as string) : defaultOrder;
    if (pageQuery > 1) {
      for (let i = 2; i <= pageQuery; i++) {
        await loadMore({ page: i, limit: limitQuery, order: orderQuery });
      }
    }
    console.warn(
      "The current route does not match the initial listing. Changing the route to match the initial listing."
    );
    // limit.value = limitQuery;
    // await changeCurrentPage(
    //   pageQuery,
    //   newQuery as unknown as operations["searchPage post /search"]["body"]
    // );
  }
};

setInitialListing(
  props?.content?.data?.listing as Schemas["ProductListingResult"]
);

let productData: any = computed(() => getElements.value);

const loadMore = async (
  query: { page?: number; limit?: number; order?: string } = {}
) => {
  //isLoading.value = true;
  const productQuery = Object.keys(query).length > 0 ? query.page : null;

  if (
    !loading.value &&
    getTotalPagesCount.value > 1 &&
    getCurrentPage.value !== getTotalPagesCount.value
  ) {
    const properties =
      getCurrentFilters.value?.properties.length > 0
        ? getCurrentFilters.value.properties.join("|")
        : "";
    const page =
      Object.keys(route.query).length > 0 && route.query.page
        ? parseInt(route.query.page as string) + 1
        : 2;

    const { limit: limitListing } = data?.listing ?? {};

    const body = {
      limit: limitListing,
      p: productQuery ? productQuery : page,
      properties: properties,
      search: "",
    };

    try {
      const currentProducts = await getProductList(categoryId, body);
      if (currentProducts && Array.isArray(currentProducts.elements)) {
        productData.value.push(...currentProducts.elements); // Append new products to the existing list
        productListTracking(productData.value);
      } else {
        throw new Error(
          "Failed to fetch products or data format is incorrect."
        );
      }

      await router.push({
        query: {
          ...route.query,
          page: productQuery ? productQuery : page,
        },
      });
    } catch (error) {
      console.error("Failed to load more products:", error);
    }
  }
  //isLoading.value = false;
};

compareRouteQueryWithInitialListing();

const { historyState } = useHistoryNavigation()

onMounted(async () => {
  const isBack = historyState.isBackNavigation;
  const savedScrollPosition = sessionStorage.getItem('listingScrollPosition') 
    ? parseInt(sessionStorage.getItem('listingScrollPosition') || '0')
    : 0;

  if (isBack && savedScrollPosition) {
    isLoading.value = true;
    window.scrollTo({
        top: 0,
        behavior: 'auto'
    });

    // Add a temporary style to prevent any scrolling during the setup phase
    const styleElement = document.createElement('style');
    styleElement.id = 'temp-scroll-lock';
    styleElement.textContent = 'html, body { overflow: hidden !important; height: 100% !important; opacity: 0.75; }';
    document.head.appendChild(styleElement);
  }
   
  // Load initial pages if needed for any navigation type
  if (Object.keys(route.query).length > 0) {
    await loadInitalPagesFromRequest(route?.query);
  }

  // For back navigation, restore scroll position
  if (isBack && savedScrollPosition) {
    // Small delay to ensure content is loaded
    setTimeout(() => {
      // Remove scroll lock for normal navigation
      const lockStyle = document.getElementById('temp-scroll-lock');
      if (lockStyle) {
        lockStyle.remove();
      }
      
      window.scrollTo({
        top: savedScrollPosition,
        behavior: 'auto'
      });
      isLoading.value = false;
    }, 100);
  }
   

  isLoading.value = false;
  productListTracking(productData.value);
});

// Simplified scroll position saving
const savedScrollPositionTrigger = () => {
  if (typeof window !== 'undefined') {
    const scrollPosition = window.scrollY;
    sessionStorage.setItem('listingScrollPosition', scrollPosition.toString());
  }
};

const skeletonCount = ref(defaultLimit);

const loadInitalPagesFromRequest = async (query: Object = {}) => {
  isLoading.value = true;
  if (!loading.value && getTotalPagesCount.value > 1 && getCurrentPage.value !== getTotalPagesCount.value) {
    const properties = getCurrentFilters.value?.properties.length > 0 ? getCurrentFilters.value.properties : "";
    const manufacturer = getCurrentFilters.value?.manufacturer.length > 0 ? getCurrentFilters.value.manufacturer.join('|') : "";
    const page = Object.keys(route.query).length > 0 && route.query.page ? parseInt(route.query.page) + 1 : 2;
    skeletonCount.value = defaultLimit * (page - 1);

    try {
      for (let i = 2; i < page; i++) {
        const body = {
          limit: defaultLimit,
          p: i,
          properties: properties,
          manufacturer: manufacturer,
          search: ""
        };

        const currentProducts = await getProductList(categoryId, body);
        if (currentProducts && Array.isArray(currentProducts.elements)) {
          // Check for duplicates before adding
          const newProducts = currentProducts.elements.filter(prod => 
            !productData.value.some(existingProd => existingProd.id === prod.id)
          );
          productData.value.push(...newProducts);
        } else {
          throw new Error('Failed to fetch products or data format is incorrect.');
        }
      }
    } catch (error) {
      console.error('Failed to load more products:', error);
    }
  }
  isLoading.value = false;
};
</script>

<template>
  <!-- Sort By and Show More Products -->
  <div class="product-listing !m-0"> 
    <div class="flex flex-col md:flex-row justify-between items-start md:items-center mb-6 pb-6 md:pb-0 border-b border-b-custom-grey md:border-0">
      <div class="w-full md:w-auto text-sm md:text-base font-normal md:font-bold mb-4 md:mb-0">
        {{ getTotal }} {{ $t("product.results") }} 
      </div>
      <div class="w-full md:w-auto flex items-center justify-between md:justify-end gap-2 sort">
        <label for="sort" class="font-bold hidden md:block">{{ $t("product.sortBy") }}:</label>
        <select
          id="sort"
          class="text-sm text-davy-grey form-select bg-gray-200 border border-custom-grey rounded-lg py-2 pl-2 pr-8 focus:outline-none bg-white w-[180px]"
          name="sort-option"
          @change="
            currentSortingOrder = ($event.target as HTMLSelectElement).value
          "
          :placeholder="$t('product.sortBy')"
        >
          <template v-for="sorting in getSortingOrders" :key="sorting.key">
            <option
              :value="sorting.key"
              :selected="sorting.key === getCurrentSortingOrder"
              class="truncate"
            >
              {{ sorting.label }}
            </option>
          </template>
        </select>
        <i class="fa-solid fa-chevron-down text-dark-bg text-xs absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none"></i>
        <button class="btn-w-icon text-sm text-davy-grey form-select bg-gray-200 border border-custom-grey rounded-lg py-2 pl-4 pr-8 focus:outline-none bg-white md:hidden" @click="toggleFilter">
          <span class="mr-2">Filter</span>
          <img src="assets/icon/filter-icon.svg" class="w-4 h-4" alt="filter icon" />
        </button>
      </div>
    </div>

    <div
      v-if="getElements.length === 0"
      class="flex flex-col items-center justify-center py-16 px-4 text-center"
    >
      <svg
        class="w-16 h-16 mb-4 text-gray-400"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="2"
          d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z"
        />
      </svg>
      <h3 class="mb-2 text-xl font-semibold text-gray-900">
        {{ $t("product.noProductsFound") }}
      </h3>
      <p class="text-gray-500">
        {{ $t("product.tryAdjustingFilters") }}
      </p>
    </div>

    <div v-else id="product-list" class="cust-product-list-3 -mx-2 md:-mx-5">
      <template v-if="!loading">
        <ProductCard
          v-for="(product, index) in getElements"
          :key="product.id"
          :product="product"
          :is-product-listing="isProductListing"
          class="border-custom-grey px-2 md:px-5 border-0"
          :class="{
            'sm:border-0 sm:border-r': index % 2 === 0,
            'lg:border-0': index % 3 === 2,
            'lg:border-r': index % 3 !== 2,
          }"
          @click="savedScrollPositionTrigger"
        />
      </template>

      <template v-if="loading">
        <ProductCardSkeleton v-for="index in limit" :key="index" />
      </template>
    </div>

    <div
      class="show-more text-center mb-5"
      :class="{ hidden: productData.length == getTotal }"
    >
      <button
        id="show-more"
        class="btn-default btn-transprent-bg bg-transparent"
        @click="loadMore()"
        :disabled="isLoading"
      >
        <!-- Show More Products -->
        <span v-if="isLoading">{{ $t("product.load.isLoading") }}</span>
        <span v-else>{{ $t("product.load.more") }}</span>
      </button>
    </div>

    <div
      class="progress shown w-full md:w-[360px] mx-auto"
      :class="{ hidden: productData.length == getTotal }"
    >
      <div class="w-full bg-platinum h-[6px] rounded mb-5 relative">
        <!-- Progress bar itself -->
        <div
          id="progress-bar"
          class="bg-arsenic h-[4px] rounded absolute top-1/2 inset-y-1/2 mt-[-2px]"
          :style="{ width: `${(productData.length / getTotal) * 100}%` }"
        ></div>
      </div>

      <!-- Progress text -->
      <div
        class="progress-shown flex items-center justify-center text-sm font-semibold text-davy-grey space-x-2"
      >
        <div id="progress-text">
          {{ productData.length }} {{ $t("product.load.of") }} {{ getTotal }}
        </div>
        <span>{{ $t("product.load.shown") }}</span>
      </div>
    </div>
  </div>
</template>
<style>
html {
  scroll-behavior: smooth;
}
</style>